import manifest from "@/marketing/documentation/manifest.json";
import type {
  HelpArticle,
  HelpCategory,
  HelpCategoryId,
  HelpManifestRecord,
} from "@/marketing/documentation/types";

export const helpCategories: HelpCategory[] = [
  { id: "start", label: "Start", title: "Start with RAQOZ", description: "Set up the workspace, learn the language and run one bounded purchase." },
  { id: "roles", label: "Roles", title: "Work by role", description: "Understand what each supported role is responsible for and where duties stay separate." },
  { id: "workflows", label: "Workflows", title: "Run the purchase journey", description: "Follow requisition, approval, sourcing, order, receipt, invoice and evidence steps." },
  { id: "administration", label: "Administration", title: "Configure the organisation", description: "Maintain structure, access, budgets, approvals, policies and bounded exceptions." },
  { id: "controls", label: "Controls", title: "Review controls and evidence", description: "Use Control Passports, supplier controls, evidence maps, exceptions and timelines." },
  { id: "imports", label: "Imports", title: "Import governed data", description: "Map, validate and record supplier, inventory, control-matrix and historical imports." },
  { id: "troubleshooting", label: "Troubleshooting", title: "Resolve common problems", description: "Diagnose invitations, access, approvals, supplier links, imports, matching and exports." },
  { id: "security", label: "Security", title: "Protect access and evidence", description: "Understand tenant-scoped access, audit events and private procurement documents." },
];

const prerequisitesByCategory: Record<HelpCategoryId, string[]> = {
  start: ["Access to a work email address.", "Authority to create or join the intended organisation."],
  roles: ["An active membership in the intended organisation.", "The role described in this article, or administrator access for review."],
  workflows: ["The preceding purchase record exists in the intended organisation.", "Your role permits the action and any assignment is addressed to you."],
  administration: ["Organisation administrator access, or another role explicitly permitted for the setting.", "The organisation has agreed the control or operating decision before configuration."],
  controls: ["Access to the relevant purchase, supplier or policy record.", "Enough source evidence to review the result without relying on copied summaries."],
  imports: ["A source file you are authorised to use.", "Stable identifiers and clean headers for the records being imported."],
  troubleshooting: ["The affected organisation, record and user are known.", "You can describe the expected and actual state without sharing credentials."],
  security: ["Access only to the organisation and records you are authorised to review."],
};

const issuesByCategory: Record<HelpCategoryId, HelpArticle["issues"]> = {
  start: [
    { problem: "The expected workspace or setup screen is not visible.", resolution: "Confirm the signed-in account, organisation membership and assigned role. Ask an organisation administrator to correct membership rather than creating a duplicate tenant." },
    { problem: "The team is unsure whether setup is ready.", resolution: "Run one bounded purchase and review the retained evidence before inviting wider use." },
  ],
  roles: [
    { problem: "A screen or action is missing.", resolution: "Check the active organisation, assigned role and record-specific assignment. Navigation is role-filtered, but the server-side permission check is the security boundary." },
    { problem: "A person needs broader access for one task.", resolution: "Use the narrowest supported role or governed exception. Do not share accounts or leave elevated access in place." },
  ],
  workflows: [
    { problem: "The next action is not available.", resolution: "Confirm the current record state, your role or assignment, and any missing policy evidence. Resolve the source condition rather than creating a parallel record." },
    { problem: "The purchase needs an exception.", resolution: "Keep the original result visible and use the configured correction, waiver or override path with an attributable reason." },
  ],
  administration: [
    { problem: "A configuration change behaves differently than expected.", resolution: "Review scope, effective date, inheritance and status. Use a representative test or no-write simulation before activating a policy change." },
    { problem: "A prior transaction appears to use an older rule.", resolution: "That may be correct. Effective-dated versions preserve the policy context used at the time instead of rewriting history." },
  ],
  controls: [
    { problem: "Evidence is marked missing.", resolution: "Open the mapped source record and either add the legitimately missing evidence or preserve the exception. Do not substitute an unsupported conclusion." },
    { problem: "A control result must be allowed exceptionally.", resolution: "Request a bounded, independent waiver or override where supported; keep the original evaluation and rationale connected." },
  ],
  imports: [
    { problem: "Rows fail validation.", resolution: "Correct required fields, formats or mappings in the source and validate again. Review duplicate treatment before any write." },
    { problem: "Imported history appears incomplete.", resolution: "Check mapping and row outcomes. Historical data remains provenance-labelled evidence and must not create live approvals, receipts or payment states." },
  ],
  troubleshooting: [
    { problem: "The problem continues after these checks.", resolution: "Record the organisation, affected record ID, expected state, actual state and time, then contact support without sending passwords, one-time codes, bank credentials or confidential documents." },
    { problem: "A workaround would bypass the control.", resolution: "Do not use the workaround. Keep the blocked or exceptional state visible and escalate through the accountable product or support path." },
  ],
  security: [
    { problem: "Unauthorised or cross-tenant access is suspected.", resolution: "Stop sharing or exporting the affected data, preserve the relevant time and record details, and report the incident through the authorised support or security channel." },
    { problem: "A document must be sent outside RAQOZ.", resolution: "Use the organisation's approved export and secure-transfer process. Protect the file after download and retain the review purpose." },
  ],
};

const records = manifest as HelpManifestRecord[];
const byCategory = new Map<HelpCategoryId, HelpManifestRecord[]>();
for (const record of records) {
  const items = byCategory.get(record.category) ?? [];
  items.push(record);
  byCategory.set(record.category, items);
}

export const helpArticles: HelpArticle[] = records.map((record) => {
  const siblings = byCategory.get(record.category) ?? [];
  const index = siblings.findIndex((item) => item.path === record.path);
  const related = [siblings[index - 1]?.path, siblings[index + 1]?.path]
    .filter((path): path is string => Boolean(path));
  return {
    ...record,
    slug: record.path.slice("/help/".length),
    metaTitle: record.title + " — RAQOZ Help Centre",
    metaDescription: record.summary,
    prerequisites: prerequisitesByCategory[record.category],
    outcome: "Use RAQOZ to " + record.summary.charAt(0).toLowerCase() + record.summary.slice(1),
    issues: issuesByCategory[record.category],
    related,
  };
});

const articleByPath = new Map(helpArticles.map((article) => [article.path, article]));

export function helpArticleByPath(pathname: string) {
  const clean = pathname.replace(/\/+$/, "") || "/";
  return articleByPath.get(clean);
}

function searchableText(article: HelpArticle) {
  return [article.title, article.summary, article.audience.join(" "), article.keywords.join(" ")]
    .join(" ")
    .toLocaleLowerCase();
}

export function searchHelpArticles(query: string, category?: HelpCategoryId) {
  const terms = query.trim().toLocaleLowerCase().split(/\s+/).filter(Boolean);
  return helpArticles.filter((article) => {
    if (category && article.category !== category) return false;
    if (!terms.length) return true;
    const text = searchableText(article);
    return terms.every((term) => text.includes(term));
  });
}

export const helpPageMeta = helpArticles.map((article) => ({
  path: article.path,
  title: article.metaTitle,
  description: article.metaDescription,
  robots: "index,follow" as const,
  status: "AVAILABLE" as const,
  changeFrequency: "monthly" as const,
  priority: 0.62,
}));

if (articleByPath.size !== 51 || articleByPath.size !== helpArticles.length) {
  throw new Error("RAQOZ help catalogue must contain exactly 51 unique routes.");
}
